import * as React from 'react';
import { useMemo } from 'react';
import * as R from 'ramda';
import { useMsgText } from '../../localization/hooks';
import { useFormik } from 'formik';
import { setupSecurityQuestionFormValidation } from '../formValidations';
import { css } from '@emotion/core';
import { FLEX_COLUMN, FLEX_ROW } from '../../layout/flexBox';
import { FORM_COLUMN } from '../../forms/styles/form';
import { ERROR_MSG } from '../../forms/styles/messages';
import LocalInput from '../../localization/LocalInput';
import { INPUT } from '../../forms/styles/input';
import LocalBtn from '../../localization/LocalBtn';
import { btnStyle } from '../../forms/styles/button';
import { Str } from '../../localization/translationKeys';
import { SELECT } from '../../forms/styles/select';
import { SecurityQuestionKey } from '../securityQuestion/models';
import LocalDropdownOption from '../../localization/LocalDropdownOption';
import Msg from '../../localization/Msg';
import LocalSelect from '../../localization/LocalSelect';
import { COLORS } from '../../globalStyle/colors';
import { useAppState } from '../../core/redux/hooks';


type Props = {
  className?: string;
  securityQuestionKeys: SecurityQuestionKey[];
  createSecurityChallenge: (questionId: number, answer: string) => void;
  isLoadingCreationQuestionResponse: boolean;
  errorCreatingSecurityChallenge: boolean;
};


/**
 * SecurityQuestionSetupForm: a component that provides a form for the user to select a security question and provide a
 * (non-empty) answer.
 *
 * PureComponent: true
 * Connected to Redux: true
 */
const CreateSecurityQuestionForm: React.FC<Props> = props => {
  const questionMsg      = useMsgText(Str.Question);
  const questionRequired = useMsgText(Str.SelectQuestion);
  const ansReqsNotMet    = useMsgText(Str.AnswerRequired);

  const { handleSubmit, handleChange, isValid, errors, values } = useFormik({
    initialValues:    {
      questionId: 0,
      answer:     '',
    },
    // Parse the question Id explicitly as a number here to prevent it from being passed as a string
    onSubmit:         (values) => {
      props.createSecurityChallenge(Number(values.questionId), values.answer);
    },
    validationSchema: setupSecurityQuestionFormValidation(questionRequired, ansReqsNotMet),
    validateOnChange: false,
  });

  const renderQuestionOptions = useMemo(
    () => R.map(
      key => (
        <LocalDropdownOption
          key={key.questionId}
          value={key.questionId}
          labelKey={key.key}
        />),
      props.securityQuestionKeys,
    ),
    [props.securityQuestionKeys],
  );
  
  const dir = useAppState(state => state.locale.textDirection);

  return (
    <div className={props.className}>
      <div css={css`margin-bottom: 16px;`}>
        <Msg str={Str.ChooseSecurityQuestion} />
      </div>
      <fieldset
        disabled={props.errorCreatingSecurityChallenge}
        css={[
          css`
            border: 0 none; // Chrome adds padding and margins to fieldsets by default
            margin: 0;
            padding: 0;
          `,
        ]}
      >
        <form
          onSubmit={handleSubmit}
          css={[
            FLEX_COLUMN,
            FORM_COLUMN,
          ]}
        >
          {
            (props.errorCreatingSecurityChallenge || !isValid) && (
              <div
                css={[
                  FLEX_ROW,
                  ERROR_MSG,
                  css`
                  align-items: center;
                `,
                ]}
              >
                {
                  props.errorCreatingSecurityChallenge
                    ? `${ansReqsNotMet}`
                    : `${errors.questionId ?? errors.answer}`
                }
              </div>
            )
          }
          <LocalSelect
            css={[SELECT]}
            name='questionId'
            value={values.questionId}
            onChange={handleChange}
            dir={dir}
          >
            <option dir={dir} value={0} hidden={true} label={questionMsg}>{questionMsg}</option>
            {renderQuestionOptions}
          </LocalSelect>
          <LocalInput
            placeholderTranslation={Str.Answer}
            name='answer'
            type='text'
            value={values.answer}
            css={[INPUT()]}
            onChange={handleChange}
          />
          <LocalBtn
            str={Str.Continue}
            type='submit'
            css={btnStyle.primary(COLORS.GREEN)}
          />
        </form>
      </fieldset>
    </div>
  );
};

export default CreateSecurityQuestionForm;
